/** 공급계약서 plain text → 화면용 섹션 트리 */

export type ContractBlock = {
  id: string
  title: string
  body: string
  kind: 'article' | 'annex' | 'party' | 'intro'
}

export type ContractSectionGroup = {
  id: string
  title: string
  kind: 'intro' | 'basic' | 'party' | 'annex'
  /** 동의 체크 대상이면 true */
  requiresCheck: boolean
  blocks: ContractBlock[]
}

export type ContractBodySegment =
  | { type: 'prose'; text: string }
  | { type: 'table'; headers: string[]; rows: string[][] }

const ARTICLE_RE = /^제\d+조/
const ANNEX_RE = /^별첨\s*\d+/
const PARTY_RE = /^계약당사자/

/** 옛 텍스트(표 셀이 한 줄씩) → :::table 마크다운으로 복원 */
export function normalizeContractBody(raw: string): string {
  let text = String(raw || '').replace(/\r\n/g, '\n')
  if (text.includes(':::table')) return text

  text = repairFlatTable(text, {
    marker: '사유\n주된 책임\n비용\n회수\n재배송\n환불\n',
    headers: ['사유', '주된 책임', '비용', '회수', '재배송', '환불'],
    cols: 6,
    stopPrefixes: ['식품·', '법령상'],
  })

  text = repairFlatTable(text, {
    marker: '위반유형\n기준안\n적용원칙\n',
    headers: ['위반유형', '기준안', '적용원칙'],
    cols: 3,
    stopPrefixes: ['위 기준은', '동일한 손해'],
  })

  text = repairAnnex4Form(text)
  return text
}

function repairFlatTable(
  text: string,
  opts: {
    marker: string
    headers: string[]
    cols: number
    stopPrefixes: string[]
  },
): string {
  const start = text.indexOf(opts.marker)
  if (start < 0) return text

  const after = text.slice(start + opts.marker.length)
  const lines = after.split('\n')
  const cells: string[] = []
  let consumed = 0
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]
    const trimmed = line.trim()
    if (!trimmed) {
      if (cells.length && cells.length % opts.cols === 0) {
        consumed = i
        break
      }
      continue
    }
    if (opts.stopPrefixes.some((p) => trimmed.startsWith(p)) || ANNEX_RE.test(trimmed)) {
      consumed = i
      break
    }
    cells.push(trimmed)
    consumed = i + 1
    if (cells.length >= opts.cols * 20) break
  }

  const rowCount = Math.floor(cells.length / opts.cols)
  if (rowCount < 1) return text

  const used = cells.slice(0, rowCount * opts.cols)
  const rows: string[][] = []
  for (let r = 0; r < rowCount; r += 1) {
    rows.push(used.slice(r * opts.cols, (r + 1) * opts.cols))
  }
  const md = [
    ':::table',
    `| ${opts.headers.join(' | ')} |`,
    `| ${opts.headers.map(() => '---').join(' | ')} |`,
    ...rows.map((row) => `| ${row.join(' | ')} |`),
    ':::',
  ].join('\n')

  const endIndex = start + opts.marker.length + lines.slice(0, consumed).join('\n').length
  const tailStart = text.slice(endIndex).startsWith('\n') ? endIndex : endIndex
  return `${text.slice(0, start)}${md}${text.slice(tailStart)}`
}

function repairAnnex4Form(text: string): string {
  const start = text.indexOf('별첨 4.')
  if (start < 0 || text.includes('| 행사명/판매기간 |')) return text
  const blockStart = text.indexOf('\n', start)
  if (blockStart < 0) return text
  const nextAnnex = text.indexOf('\n별첨 5.', blockStart)
  const end = nextAnnex >= 0 ? nextAnnex : text.length
  const chunk = text.slice(blockStart, end).trim()
  if (!chunk || chunk.includes(':::table')) return text
  const labels = chunk
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
  if (labels.length < 5) return text
  const md = [
    '',
    ':::table',
    '| 항목 | 내용 |',
    '| --- | --- |',
    ...labels.map((label) => `| ${label} |  |`),
    ':::',
    '',
  ].join('\n')
  return `${text.slice(0, blockStart)}${md}${text.slice(end)}`
}

function splitMarkdownRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return trimmed.split('|').map((cell) => cell.trim())
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell))
}

/** 본문 → prose / table 세그먼트 */
export function parseContractBodySegments(body: string): ContractBodySegment[] {
  const text = String(body || '').replace(/\r\n/g, '\n').trim()
  if (!text) return []

  const segments: ContractBodySegment[] = []
  const parts = text.split(/^:::table\s*$/m)
  segments.push(...proseSegments(parts[0] || ''))

  for (let i = 1; i < parts.length; i += 1) {
    const chunk = parts[i]
    const end = chunk.indexOf('\n:::')
    const tablePart = end >= 0 ? chunk.slice(0, end) : chunk
    const rest = end >= 0 ? chunk.slice(end + 4) : ''
    const table = parseMarkdownTable(tablePart)
    if (table) segments.push(table)
    else if (tablePart.trim()) segments.push({ type: 'prose', text: tablePart.trim() })
    segments.push(...proseSegments(rest.replace(/^\n:::?\s*/, '')))
  }

  return segments.filter((seg) => (seg.type === 'prose' ? Boolean(seg.text.trim()) : true))
}

function proseSegments(raw: string): ContractBodySegment[] {
  const text = raw.trim()
  return text ? [{ type: 'prose', text }] : []
}

function parseMarkdownTable(raw: string): ContractBodySegment | null {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('|'))
  if (lines.length < 2) return null
  const headers = splitMarkdownRow(lines[0])
  let dataStart = 1
  if (isSeparatorRow(splitMarkdownRow(lines[1]))) dataStart = 2
  const rows = lines.slice(dataStart).map(splitMarkdownRow).filter((row) => row.some(Boolean))
  if (!headers.length) return null
  return { type: 'table', headers, rows }
}

export function parseContractSections(raw: string): ContractSectionGroup[] {
  const text = normalizeContractBody(raw).trim()
  if (!text) return []

  const lines = text.split('\n')
  const blocks: ContractBlock[] = []
  let current: ContractBlock | null = null
  let articleIndex = 0
  let annexIndex = 0

  const push = () => {
    if (!current) return
    current.body = current.body.replace(/\n{3,}/g, '\n\n').trim()
    blocks.push(current)
    current = null
  }

  for (const line of lines) {
    const trimmed = line.trim()
    if (ARTICLE_RE.test(trimmed)) {
      push()
      articleIndex += 1
      current = {
        id: `article-${articleIndex}`,
        title: trimmed,
        body: '',
        kind: 'article',
      }
      continue
    }
    if (ANNEX_RE.test(trimmed)) {
      push()
      annexIndex += 1
      current = {
        id: `annex-${annexIndex}`,
        title: trimmed,
        body: '',
        kind: 'annex',
      }
      continue
    }
    if (PARTY_RE.test(trimmed)) {
      push()
      current = {
        id: 'party',
        title: trimmed,
        body: '',
        kind: 'party',
      }
      continue
    }
    if (!current) {
      current = {
        id: 'intro',
        title: trimmed || '계약서',
        body: '',
        kind: 'intro',
      }
      continue
    }
    if (current.kind === 'intro' && !current.body && ARTICLE_RE.test(trimmed) === false) {
      if (!current.body && current.title && trimmed) {
        current.body = trimmed
        continue
      }
    }
    current.body = current.body ? `${current.body}\n${line}` : line
  }
  push()

  const intro = blocks.filter((b) => b.kind === 'intro')
  const articles = blocks.filter((b) => b.kind === 'article')
  const party = blocks.filter((b) => b.kind === 'party')
  const annexes = blocks.filter((b) => b.kind === 'annex')

  const groups: ContractSectionGroup[] = []
  if (intro.length) {
    groups.push({
      id: 'intro',
      title: intro[0].title,
      kind: 'intro',
      requiresCheck: false,
      blocks: intro,
    })
  }
  if (articles.length) {
    groups.push({
      id: 'basic',
      title: '기본계약 (제1조 ~ 제16조)',
      kind: 'basic',
      requiresCheck: true,
      blocks: articles,
    })
  }
  if (party.length) {
    groups.push({
      id: 'party',
      title: '계약당사자 및 전자동의 확인',
      kind: 'party',
      requiresCheck: true,
      blocks: party,
    })
  }
  annexes.forEach((block) => {
    groups.push({
      id: block.id,
      title: block.title,
      kind: 'annex',
      requiresCheck: true,
      blocks: [block],
    })
  })

  return groups
}
